/** Variables passed to a GraphQL operation. */
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export type GraphqlVariables = {};

/** Key selecting which Amplify client to use. */
export type AmplifyClientKey = "appsyncBff" | "springBff";

/** Standard async-operation result — framework-agnostic. */
export interface GraphqlResult<TData> {
  data: TData | undefined;
  loading: boolean;
  error: Error | undefined;
}

/** Resolved value returned by trigger / mutate functions. */
export interface TriggerResult<TData> {
  data: TData | undefined;
}

export type LazyQueryTrigger<TData, TVariables> = (options?: {
  variables?: TVariables;
}) => Promise<TriggerResult<TData>>;

export type MutationTrigger<TData, TVariables> = (options?: {
  variables?: TVariables;
}) => Promise<TriggerResult<TData>>;

/**
 * GraphQL error shape for Amplify errors.
 * Amplify does not distinguish between network and application errors —
 * an error is an error. All errors surface through `graphQLErrors` or
 * as a plain Error with a message.
 */
export interface GraphqlErrorLike {
  message: string;
  graphQLErrors?: ReadonlyArray<{
    message: string;
    extensions?: Record<string, unknown>;
  }>;
}

/** Type guard: does this error carry GraphQL error details?
 * Duck-typing via `.graphQLErrors` — Amplify throws raw { data, errors }
 * objects that aren't Error instances. */
export const isGraphqlErrorLike = (error: unknown): error is GraphqlErrorLike =>
  typeof error === "object" &&
  error !== null &&
  "message" in error &&
  "graphQLErrors" in error;

/**
 * Coerce an unknown thrown value into an Error.
 * Amplify's client.graphql() throws the raw { data, errors } response object
 * on GraphQL errors rather than an Error instance.
 *
 * Preserves the full `errors` array as `graphQLErrors` on the resulting Error
 * so that consumers checking `error.graphQLErrors` (e.g. for errorType/errorInfo)
 * continue to work correctly after the Apollo → Amplify migration.
 */
export function toError(err: unknown): Error {
  if (err instanceof Error) return err;
  if (
    typeof err === "object" &&
    err !== null &&
    "errors" in err &&
    Array.isArray((err as {errors: unknown[]}).errors)
  ) {
    const errors = (err as {errors: Array<Record<string, unknown>>}).errors;
    const message = errors
      .map(e => (typeof e.message === "string" ? e.message : "Unknown error"))
      .join("; ");
    const error = new Error(message) as Error & {graphQLErrors: typeof errors};
    error.graphQLErrors = errors;
    return error;
  }
  return new Error(String(err));
}
